/**
 * Assainissement du texte enrichi saisi dans l'application.
 *
 * <p>La sous-désignation d'une ligne se compose dans un éditeur enrichi : on y
 * met du gras, une couleur, un retour à la ligne. Elle est donc rendue comme du
 * HTML — et l'était jusqu'ici telle quelle.
 *
 * <p>Or un collage depuis une page web apporte le balisage de la source, sans
 * rien en retirer. Il suffisait d'y coller ce qu'il fallait pour qu'un script
 * s'exécute — non seulement chez l'artisan, mais chez le client, qui ouvre le
 * devis par un lien pour le signer.
 *
 * <p>On ne garde donc que ce qui sert à la mise en forme, et rien d'autre.
 */

/** Balises conservées : celles que l'éditeur produit, et elles seules. */
const BALISES = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'STRIKE',
  'BR',
  'P',
  'DIV',
  'SPAN',
  'UL',
  'OL',
  'LI',
  'FONT',
]);

/**
 * Balises dont le contenu part avec elles.
 *
 * <p>Ailleurs on garde le texte d'une balise écartée — un paragraphe collé doit
 * conserver ses mots. Ici non : le « texte » d'un script est le script.
 */
const MUETTES = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT']);

/** Propriétés de style conservées : l'apparence, jamais le comportement. */
const STYLES = new Set([
  'color',
  'background-color',
  'font-weight',
  'font-style',
  'font-size',
  'text-decoration',
  'text-align',
]);

/** Ne retient d'une déclaration de style que les propriétés d'apparence. */
function styleRetenu(valeur) {
  return String(valeur || '')
    .split(';')
    .map((d) => d.trim())
    .filter(Boolean)
    .filter((d) => {
      const [propriete, ...reste] = d.split(':');
      const contenu = reste.join(':').toLowerCase();
      // `url(...)` et `expression(...)` permettent de charger ou d'exécuter :
      // une couleur n'en a pas besoin.
      if (contenu.includes('url(') || contenu.includes('expression(')) {
        return false;
      }
      return STYLES.has(propriete.trim().toLowerCase());
    })
    .join('; ');
}

/**
 * Retire d'un fragment enrichi tout ce qui n'est pas de la mise en forme.
 *
 * @param {string} html fragment tel que l'éditeur l'a produit
 * @returns {string} le même fragment, réduit aux balises et styles admis
 */
export function nettoieHtml(html) {
  const source = String(html ?? '');
  if (!source.trim()) {
    return '';
  }
  if (typeof DOMParser === 'undefined') {
    // Hors navigateur — rendu de secours, impression, test : on ne garde que le
    // texte, faute de pouvoir analyser le balisage.
    return source.replace(/<[^>]*>/g, '');
  }

  const document_ = new DOMParser().parseFromString('<body>' + source + '</body>', 'text/html');

  const visite = (noeud) => {
    for (const enfant of [...noeud.childNodes]) {
      if (enfant.nodeType === 3) {
        continue; // texte : conservé tel quel
      }
      if (enfant.nodeType !== 1) {
        enfant.remove(); // commentaire, instruction de traitement…
        continue;
      }
      if (MUETTES.has(enfant.tagName)) {
        enfant.remove();
        continue;
      }
      if (!BALISES.has(enfant.tagName)) {
        // La balise disparaît, son texte reste : un paragraphe collé garde ses
        // mots même si son enveloppe n'est pas admise.
        const remplacant = document_.createTextNode(enfant.textContent || '');
        enfant.replaceWith(remplacant);
        continue;
      }
      for (const attribut of [...enfant.attributes]) {
        const nom = attribut.name.toLowerCase();
        if (nom === 'style') {
          const retenu = styleRetenu(attribut.value);
          if (retenu) {
            enfant.setAttribute('style', retenu);
          } else {
            enfant.removeAttribute('style');
          }
          continue;
        }
        if (nom === 'color' && enfant.tagName === 'FONT') {
          continue;
        }
        // Tout le reste part : `onerror`, `src`, `href`, `srcdoc`…
        enfant.removeAttribute(attribut.name);
      }
      visite(enfant);
    }
  };

  visite(document_.body);
  return document_.body.innerHTML;
}

/**
 * Fragment enrichi prêt à être injecté.
 *
 * @param {string} html fragment enrichi
 * @returns {{__html: string}} la valeur attendue par React
 */
export function enrichi(html) {
  return { __html: nettoieHtml(html) };
}

/**
 * Texte brut d'une description de ligne.
 *
 * <p>La description se saisit désormais en texte simple. Les devis écrits avant
 * portent encore du HTML : rendu tel quel dans une zone de texte, il montrait
 * ses balises au lieu de son contenu. Il est ramené au texte, sauts de ligne
 * conservés, pour que l'ancien et le nouveau se lisent de la même façon.
 *
 * @param {string} valeur description, texte ou HTML d'avant
 * @returns {string} le texte, avec ses retours à la ligne
 */
export function texteDescription(valeur) {
  const brut = valeur == null ? '' : String(valeur);
  if (!/<[a-z][\s\S]*>/i.test(brut)) {
    return brut;
  }
  if (typeof DOMParser === 'undefined') {
    // Hors navigateur — le rendu du PDF officiel tourne sous Node : pas
    // d'analyseur, mais le document ne doit pas montrer ses balises. Les fins
    // de bloc valent un retour à la ligne, le reste du balisage disparaît.
    return brut
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
  const doc = new DOMParser().parseFromString(nettoieHtml(brut), 'text/html');
  // Un <br> ou une fin de bloc vaut un retour a la ligne : c'est ce que
  // l'auteur voyait quand il l'a ecrit.
  doc.body.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
  doc.body.querySelectorAll('p, div, li').forEach((bloc) => bloc.append('\n'));
  return doc.body.textContent.replace(/\n{3,}/g, '\n\n').trim();
}
